import React, { useState, useMemo, useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { FileText, LayoutDashboard, Upload, History, FileSpreadsheet, ClipboardCheck, LogOut, User, KeyRound, Calendar, Lightbulb, Building2, ChevronDown, Share2, Search, PenLine, ScrollText, Scale, LifeBuoy, Globe, Bookmark, Zap, ShieldCheck, MessageCircleQuestion } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarProvider,
  SidebarTrigger,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";

function SidebarNavFromQuerySync() {
  const location = useLocation();
  const { setOpen } = useSidebar();
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("nav") === "collapsed") {
      setOpen(false);
    }
  }, [location.search, setOpen]);
  return null;
}

/** Botão "Pergunte a IA" no header (visível na página Resultados com edital carregado). */
function LayoutPerguntarIaButton() {
  const { edital, invokeOnUpdate } = usePerguntarIaLayout();
  const [open, setOpen] = useState(false);
  if (!edital?.id) return null;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300"
        onClick={() => setOpen(true)}
      >
        <MessageCircleQuestion className="h-4 w-4 shrink-0" />
        <span className="hidden sm:inline">Pergunte a IA</span>
      </Button>
      <PerguntarIaDialog
        edital={edital}
        open={open}
        onOpenChange={setOpen}
        onUpdate={invokeOnUpdate}
      />
    </>
  );
}
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import UserDeactivated from "@/components/UserDeactivated";
import AlterarSenhaDialog from "@/components/AlterarSenhaDialog";
import TrocarEmpresaConta from "@/components/TrocarEmpresaConta";
import PortaisLicitacaoDialog from "@/components/licitacao/PortaisLicitacaoDialog";
import LinksImportantesDialog from "@/components/licitacao/LinksImportantesDialog";
import { MODULOS_LISTA } from "@/lib/modulos";
import { resolveAllowedModulesForUser } from "@/lib/moduleAccess";
import { GuidedTourProvider, GuidedTourHeaderButton } from "@/components/guided-tour/GuidedTourProvider";
import { PerguntarIaLayoutProvider, usePerguntarIaLayout } from "@/lib/PerguntarIaLayoutContext";
import PerguntarIaDialog from "@/components/resultados/PerguntarIaDialog";
import ScrollToTopButton from "@/components/ScrollToTopButton";

const PAGE_TITLE_EXTRA = {
  CriarAtestado: "Nova declaração",
};

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const { user: authUser, empresa, logout, isLoadingAuth, empresasVinculadas } = useAuth();
  const user = authUser;
  const mostrarTrocaMulticonta = useMemo(() => {
    const alvos = (empresasVinculadas || []).filter((x) => x.podeTrocar);
    return alvos.some((x) => x.empresaId !== empresa?.id);
  }, [empresasVinculadas, empresa?.id]);
  const companies = empresa ? [empresa] : [];
  const selectedCompanyId = empresa?.id ?? null;
  const companyName = empresa?.nome ?? null;
  const [alterarSenhaOpen, setAlterarSenhaOpen] = useState(false);
  const [portaisDialogOpen, setPortaisDialogOpen] = useState(false);
  const [linksImportantesOpen, setLinksImportantesOpen] = useState(false);
  const [vizEmpresaLabelHover, setVizEmpresaLabelHover] = useState(false);
  const [scrollEl, setScrollEl] = useState(null);

  // Lista de navegação em useMemo para evitar TDZ com ícones minificados em produção (não em nível de módulo)
  const allNavigationItems = useMemo(() => [
    { title: "Empresas", url: createPageUrl("Empresas"), icon: Building2, adminOnly: true, moduleId: "Empresas" },
    { title: "Agenda", url: createPageUrl("Agenda"), icon: Calendar, moduleId: "Agenda" },
    { title: "Dashboard", url: createPageUrl("Dashboard"), icon: LayoutDashboard, moduleId: "Dashboard" },
    { title: "Novo Edital/Cotação", url: createPageUrl("Upload"), icon: Upload, moduleId: "Upload" },
    { title: "Consultar Editais", url: createPageUrl("ConsultarEditais"), icon: Search, moduleId: "ConsultarEditais" },
    { title: "Análise e Aprovação", url: createPageUrl("Analise"), icon: ClipboardCheck, moduleId: "Analise" },
    { title: "Licitação", url: createPageUrl("Licitacao"), icon: FileSpreadsheet, moduleId: "Licitacao" },
    { title: "Propostas", url: createPageUrl("Propostas"), icon: FileText, moduleId: "Propostas" },
    { title: "Atestados Técnicos", url: createPageUrl("AtestadosTecnicos"), icon: FileText, moduleId: "AtestadosTecnicos" },
    { title: "Histórico", url: createPageUrl("Historico"), icon: History, moduleId: "Historico" },
    { title: "Contratos", url: createPageUrl("Contratos"), icon: FileText, moduleId: "Contratos" },
    { title: "Documentos", url: createPageUrl("Documentos"), icon: FileText, moduleId: "Documentos" },
    { title: "Declarações", url: createPageUrl("AtestadosDeclaracoes"), icon: ScrollText, moduleId: "AtestadosDeclaracoes" },
    { title: "Assina", url: createPageUrl("Assina"), icon: PenLine, moduleId: "Assina" },
    { title: "AuctionCertify", url: createPageUrl("AuctionCertify"), icon: ShieldCheck, moduleId: "AuctionCertify" },
    { title: "Manifesto", url: createPageUrl("Manifesto"), icon: Scale, moduleId: "Manifesto" },
    { title: "Status Report", url: createPageUrl("StatusReport"), icon: FileSpreadsheet, moduleId: "StatusReport" },
    { title: "Dashboard Comercial", url: createPageUrl("Relatorios"), icon: FileSpreadsheet, moduleId: "Relatorios" },
    { title: "Lições Aprendidas", url: createPageUrl("LearnedLessons"), icon: Lightbulb, moduleId: "LearnedLessons" },
    { title: "Suporte", url: createPageUrl("Suporte"), icon: LifeBuoy, moduleId: "Suporte" },
    { title: "Social", url: createPageUrl("Social"), icon: Share2, moduleId: "Social" },
    { title: "Pulse — Automação", url: createPageUrl("Pulse"), icon: Zap, moduleId: "Pulse" },
  ], []);

  React.useEffect(() => {
    if (document.title.startsWith("Proposta de Preço")) return;
    const navItem = allNavigationItems.find((i) => i.moduleId === currentPageName);
    const modItem = MODULOS_LISTA.find((m) => m.id === currentPageName);
    const segment =
      navItem?.title ?? PAGE_TITLE_EXTRA[currentPageName] ?? modItem?.label;
    document.title = segment
      ? `${segment} | Licitações - Auction`
      : "Licitações - Auction";
  }, [location.pathname, currentPageName, allNavigationItems]);

  const handleCompanyChange = (companyId) => {
    localStorage.setItem('admin_selected_company_id', companyId);
    window.location.reload();
  };

  const handleLogout = () => {
    logout(true);
  };

  const getFilteredNavigationItems = () => {
    // Enquanto a autenticação está carregando, não mostrar menu completo (evita flash com todos os itens)
    if (isLoadingAuth) {
      return [];
    }
    if (!user) {
      return allNavigationItems.filter(item => !item.adminOnly);
    }
    const isAdmin = user.role === 'admin';
    const { basePlanoMods, allowedModules } = resolveAllowedModulesForUser(user, empresa);

    const filtered = allNavigationItems.filter((item) => {
      if (item.adminOnly && !isAdmin) return false;
      if (item.moduleId) {
        if (basePlanoMods !== undefined) {
          if (!Array.isArray(allowedModules) || allowedModules.length === 0) return false;
          return (
            allowedModules.includes(item.moduleId) ||
            (item.moduleId === "AtestadosDeclaracoes" && allowedModules.includes("Documentos")) ||
            (item.moduleId === "Documentos" && allowedModules.includes("AtestadosDeclaracoes"))
          );
        }
        if (user.role === "operador" && Array.isArray(allowedModules) && allowedModules.length === 0) {
          return false;
        }
        if (Array.isArray(allowedModules) && allowedModules.length > 0) {
          return (
            allowedModules.includes(item.moduleId) ||
            (item.moduleId === "AtestadosDeclaracoes" && allowedModules.includes("Documentos")) ||
            (item.moduleId === "Documentos" && allowedModules.includes("AtestadosDeclaracoes"))
          );
        }
        return true;
      }
      return true;
    });
    // Ordenar: operador usa ordem de user.permissoes.modulos; demais usam ordem do plano
    const ordemMenu =
      user.role === 'operador' && Array.isArray(user.permissoes?.modulos)
        ? user.permissoes.modulos
        : basePlanoMods !== undefined
          ? Array.isArray(allowedModules) && allowedModules.length > 0
            ? allowedModules
            : []
          : Array.isArray(empresa?.plano?.modulosHabilitados)
            ? empresa.plano.modulosHabilitados
            : [];
    if (ordemMenu.length > 0) {
      const ordemMap = new Map(ordemMenu.map((id, i) => [id, i]));
      return [...filtered].sort((a, b) => {
        const ia = ordemMap.has(a.moduleId) ? ordemMap.get(a.moduleId) : 9999;
        const ib = ordemMap.has(b.moduleId) ? ordemMap.get(b.moduleId) : 9999;
        return ia - ib;
      });
    }
    return filtered;
  };

  const navigationItems = getFilteredNavigationItems();

  const sidebarCollapsedFromQuery =
    new URLSearchParams(location.search).get("nav") === "collapsed";

  // Se usuário está desativado, mostrar tela específica
  if (user?.deactivated) {
    return <UserDeactivated />;
  }

  /** Cadastro pago pendente de checkout: só fluxo de pagamento, sem menu lateral. */
  const checkoutPagamentoSlim =
    empresa?.status === 'awaiting_payment' && currentPageName === 'AssinaturaPlano';

  if (checkoutPagamentoSlim) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 to-blue-50 py-6 px-4">
        <div className="max-w-lg mx-auto pb-4">
          <TrocarEmpresaConta />
        </div>
        {children}
        <Toaster />
        <AlterarSenhaDialog open={alterarSenhaOpen} onOpenChange={setAlterarSenhaOpen} />
      </div>
    );
  }

  if (empresa?.status === 'awaiting_payment' && currentPageName !== 'AssinaturaPlano') {
    return <Navigate to={`/assinaturaplano${location.search || ''}`} replace />;
  }

  return (
    <SidebarProvider defaultOpen={!sidebarCollapsedFromQuery}>
      <PerguntarIaLayoutProvider>
      <SidebarNavFromQuerySync />
      <div className="min-h-screen flex w-full bg-gradient-to-br from-slate-50 to-blue-50">
        <Sidebar className="border-r border-slate-200/60 backdrop-blur-sm bg-white" collapsible="icon" data-tour="nav-sidebar">
          <SidebarHeader className="border-b border-slate-200/60 p-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68e50deef22388676f24e5ca/058df03c3_LogoAuction.png"
                alt="Auction Logo"
                className="h-10 w-10 object-contain flex-shrink-0"
              />
              <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                <h2 className="font-bold text-xl text-slate-900">Auction</h2>
                <p className="text-xs text-slate-500 font-medium">Licitações Inteligentes</p>
              </div>
            </div>
          </SidebarHeader>
          
          <SidebarContent className="p-3">
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-3 group-data-[collapsible=icon]:hidden">
                Navegação
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {isLoadingAuth && navigationItems.length === 0 ? (
                    <SidebarMenuItem>
                      <div className="flex items-center gap-3 px-4 py-3 text-sm text-slate-500">
                        <div className="h-5 w-5 rounded bg-slate-200 animate-pulse" />
                        <span className="group-data-[collapsible=icon]:hidden">Carregando...</span>
                      </div>
                    </SidebarMenuItem>
                  ) : null}
                  {navigationItems.map((item) => {
                    const isActive = location.pathname === item.url;
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton 
                          asChild 
                          className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-xl mb-1 ${
                            isActive ? 'bg-blue-600 text-white hover:bg-blue-700 hover:text-white shadow-md shadow-blue-600/30' : ''
                          }`}
                          tooltip={item.title}
                        >
                          <Link
                            to={item.url}
                            className="flex items-center gap-3 px-4 py-3"
                            {...(item.moduleId ? { 'data-tour': `nav-${item.moduleId}` } : {})}
                          >
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            <span className="font-medium group-data-[collapsible=icon]:hidden">{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-slate-200/60 p-3">
            <TrocarEmpresaConta className="mb-3 group-data-[collapsible=icon]:hidden" />
            {user && user.role === 'admin' && companies.length > 0 && !mostrarTrocaMulticonta && (
              <div className="mb-3 group-data-[collapsible=icon]:hidden">
                <label
                  className={`text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 mb-2 block ${
                    vizEmpresaLabelHover ? "" : "hidden"
                  }`}
                  aria-hidden={!vizEmpresaLabelHover}
                >
                  Visualizar Empresa
                </label>
                <Select value={selectedCompanyId || ""} onValueChange={handleCompanyChange}>
                  <SelectTrigger className="w-full" aria-label="Visualizar empresa">
                    <SelectValue placeholder="Selecione uma empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-2 border-b">
                      <Input
                        placeholder="Buscar empresa..."
                        className="h-8"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const search = e.target.value.toLowerCase();
                          const items = document.querySelectorAll('[role="option"]');
                          items.forEach((item) => {
                            const text = item.textContent.toLowerCase();
                            item.style.display = text.includes(search) ? '' : 'none';
                          });
                        }}
                      />
                    </div>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full flex flex-col items-stretch gap-1 px-4 py-6 hover:bg-slate-100 rounded-xl"
                  >
                    <div
                      className="flex items-start gap-3 w-full"
                      onMouseEnter={() => setVizEmpresaLabelHover(true)}
                      onMouseLeave={() => setVizEmpresaLabelHover(false)}
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 text-left min-w-0 group-data-[collapsible=icon]:hidden flex flex-col">
                        {companyName && (
                          <p className="text-xs text-slate-500 font-medium truncate mb-0.5">
                            {companyName}
                          </p>
                        )}
                        <p className="font-medium text-sm text-slate-900 truncate">
                          {user.full_name || user.email}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 truncate pl-11 w-full text-left group-data-[collapsible=icon]:hidden">
                      {user.email}
                    </p>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    onClick={() => setAlterarSenhaOpen(true)}
                    className="cursor-pointer"
                  >
                    <KeyRound className="w-4 h-4 mr-2" />
                    Alterar senha
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </SidebarFooter>
        </Sidebar>

        <main className="flex-1 flex flex-col">
          <GuidedTourProvider currentPageName={currentPageName}>
            <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 px-6 py-4 sticky top-0 z-10">
              <div className="flex items-center gap-4">
                <SidebarTrigger className="hover:bg-slate-100 p-2 rounded-xl transition-colors duration-200" />
                <div className="flex items-center gap-2 md:hidden">
                  <img 
                    src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68e50deef22388676f24e5ca/058df03c3_LogoAuction.png"
                    alt="Auction Logo"
                    className="h-8 w-8 object-contain"
                  />
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <LayoutPerguntarIaButton />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setPortaisDialogOpen(true)}
                  >
                    <Globe className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">Portais</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setLinksImportantesOpen(true)}
                  >
                    <Bookmark className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">Links importantes</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => {
                      window.location.href = "https://auction.elicitacao.cloud/suporte";
                    }}
                  >
                    <LifeBuoy className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">Suporte</span>
                  </Button>
                  <GuidedTourHeaderButton />
                </div>
              </div>
            </header>

            <div ref={setScrollEl} className="flex-1 overflow-auto">
              {children}
            </div>
            <ScrollToTopButton scrollContainer={scrollEl} />
            <PortaisLicitacaoDialog
              open={portaisDialogOpen}
              onOpenChange={setPortaisDialogOpen}
            />
            <LinksImportantesDialog
              open={linksImportantesOpen}
              onOpenChange={setLinksImportantesOpen}
            />
          </GuidedTourProvider>
        </main>
      </div>
      <Toaster />
      <AlterarSenhaDialog open={alterarSenhaOpen} onOpenChange={setAlterarSenhaOpen} />
      </PerguntarIaLayoutProvider>
    </SidebarProvider>
  );
}